import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Wind, Eye, AlertTriangle, Map as MapIcon } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { WindFarm, Turbine, Annotation } from '../types';
import { getProjectMetadata } from '../utils/r2Storage';
import { inspectionService } from '../services/inspectionService';
import L from 'leaflet';

// Fix leaflet default marker icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const PageContainer = styled.div`
  padding: 30px;
  max-width: 1600px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 30px;
  
  h1 {
    font-size: 32px;
    font-weight: 700;
    color: #1a1f3a;
    margin: 0 0 8px 0;
  }
  
  p {
    color: #6b7280;
    margin: 0;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div<{ $color: string }>`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 120px;
    height: 120px;
    background: ${props => props.$color};
    opacity: 0.05;
    border-radius: 50%;
    transform: translate(40%, -40%);
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
  }
`;

const StatIcon = styled.div<{ $color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  
  svg {
    width: 24px;
    height: 24px;
    color: white;
  }
`;

const StatValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: #1a1f3a;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
`;

const StatLink = styled.a`
  font-size: 12px;
  color: #0099ff;
  text-decoration: none;
  margin-top: 8px;
  display: inline-block;
  
  &:hover {
    text-decoration: underline;
  }
`;

const MapSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
  
  h2 {
    font-size: 18px;
    font-weight: 600;
    color: #1a1f3a;
    margin: 0 0 16px 0;
    display: flex;
    align-items: center;
    gap: 8px;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const MapContainer2 = styled.div`
  height: 400px;
  border-radius: 8px;
  overflow: hidden;
  
  .leaflet-container {
    height: 100%;
    border-radius: 8px;
  }
`;

const DamagesSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  h2 {
    font-size: 18px;
    font-weight: 600;
    color: #1a1f3a;
    margin: 0 0 16px 0;
  }
`;

const DamagesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th {
    text-align: left;
    padding: 12px;
    background: #f9fafb;
    color: #6b7280;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  td {
    padding: 12px;
    border-top: 1px solid #f3f4f6;
    color: #1a1f3a;
    font-size: 14px;
  }
  
  tr:hover {
    background: #f9fafb;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
`;

const Dashboard: React.FC = () => {
  const [farms, setFarms] = useState<WindFarm[]>([]);
  const [turbinesCount, setTurbinesCount] = useState(0);
  const [damages, setDamages] = useState<Annotation[]>([]);
  const [inspectionCount, setInspectionCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const metadata: any = await getProjectMetadata('farms-list');
      if (metadata && metadata.farms) {
        setFarms(metadata.farms);
        // Calculate total turbines
        const total = metadata.farms.reduce((acc: number, farm: any) => acc + (Number(farm.turbineCount) || 0), 0);
        setTurbinesCount(total);
      }

      // Fetch inspections count
      const inspections = await inspectionService.getAll();
      setInspectionCount(inspections.length);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    {
      label: 'Wind Farm(s)',
      value: farms.length,
      icon: <Wind />,
      color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
      label: 'Turbine(s)',
      value: turbinesCount,
      icon: <Wind />,
      color: 'linear-gradient(135deg, #00d4ff 0%, #0099ff 100%)',
    },
    {
      label: 'Inspection(s)',
      value: 0,
      icon: <Eye />,
      color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    },
    {
      label: 'Damage(s)',
      value: inspectionCount, // Show real inspection count here
      icon: <AlertTriangle />,
      color: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    }
  ];

  const defaultCenter: [number, number] = [39.0, 35.0]; // Turkey center

  return (
    <PageContainer>
      <Header>
        <h1>Dashboard</h1>
        <p>Overview of your wind farms and turbines ({loading ? 'Loading...' : 'Ready'})</p>
      </Header>

      <StatsGrid>
        {stats.map((stat, index) => (
          <StatCard key={index} $color={stat.color}>
            <StatIcon $color={stat.color}>
              {stat.icon}
            </StatIcon>
            <StatValue>{stat.value}</StatValue>
            <StatLabel>{stat.label}</StatLabel>
          </StatCard>
        ))}
      </StatsGrid>

      <MapSection>
        <h2>
          <MapIcon /> WIND FARM MAP
        </h2>
        <MapContainer2>
          <MapContainer
            center={defaultCenter}
            zoom={6}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {farms.map((farm) => (
              <Marker key={farm.id} position={[farm.location.lat, farm.location.lng]}>
                <Popup>
                  <strong>{farm.name}</strong><br />
                  {farm.city}, {farm.country}<br />
                  {farm.turbineCount} turbines
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </MapContainer2>
      </MapSection>

// ... rest of component ...

      <DamagesSection>
        <h2>DAMAGES LIST</h2>
        {damages.length > 0 ? (
          <DamagesTable>
            <thead>
              <tr>
                <th>Wind Farm</th>
                <th>Turbine No</th>
                <th>Damage</th>
                <th>Damage Degree</th>
              </tr>
            </thead>
            <tbody>
              {damages.map((damage) => (
                <tr key={damage.id}>
                  <td>-</td>
                  <td>{damage.imageData.turbineName}</td>
                  <td>{damage.damageType || 'Unknown'}</td>
                  <td>{damage.severity || '-'}</td>
                </tr>
              ))}
            </tbody>
          </DamagesTable>
        ) : (
          <EmptyState>
            No damages recorded yet
          </EmptyState>
        )}
      </DamagesSection>
    </PageContainer >
  );
};

export default Dashboard;
